/**
 * Scout v0 — Layer 3 per-search specifics form.
 *
 * Size band, geo, role SELECTION, batch size. Roles are NOT re-typed here:
 * layer 2 (the class template) owns the reusable role blanket, and this form
 * only picks which of those roles the current run targets (a subset — match
 * mode and persisted synonyms stay on the template). Keywords are NOT here
 * either: they belong to the layer-2 template only (layers 2 and 3 never
 * repeat fields). Size/geo remain free per-run inputs that are never stored —
 * everything composes with the chosen layer-2 template into the resolved
 * ComposedCriteria.
 *
 * NOTE on batch size: the backend run-spec has a batchSize spend cap (the
 * `results` param passed to Scrapingdog), but the P15 compose/launch HTTP
 * endpoint does NOT accept a batchSize field in its request body (the launch
 * route constructs the ingest door without a resultCount). So batch size is
 * captured here as an operator input for reference, but is NOT sent to the
 * backend in the compose/launch request. Wiring it to the ingest cap is a
 * separate backend task.
 */

import { useEffect, useState } from 'react';
import type { Layer3Specifics, RoleSpec } from '@/types/scout';

interface SpecificsFormProps {
  /** The selected layer-2 template's role blanket (empty when none selected). */
  templateRoles: RoleSpec[];
  /** Identity of the selected template — role selection resets when it changes. */
  templateId: string | null;
  /** Called with the current specifics whenever they change. */
  onChange: (specifics: Layer3Specifics) => void;
  /** Called with whether the form is valid for composing. */
  onValidityChange: (valid: boolean) => void;
  /** Whether the compose step is in flight (disables the form). */
  disabled?: boolean;
}

export function SpecificsForm({
  templateRoles,
  templateId,
  onChange,
  onValidityChange,
  disabled,
}: SpecificsFormProps) {
  const [sizeMin, setSizeMin] = useState('');
  const [sizeMax, setSizeMax] = useState('');
  const [geo, setGeo] = useState('');
  const [selectedTitles, setSelectedTitles] = useState<string[]>([]);
  const [batchSize, setBatchSize] = useState('20');

  // Emit the composed specifics + validity up to the parent.
  const emit = (
    nextSizeMin: string,
    nextSizeMax: string,
    nextGeo: string,
    nextSelected: string[],
  ) => {
    const size: Layer3Specifics['size'] = {};
    const min = Number(nextSizeMin);
    const max = Number(nextSizeMax);
    if (nextSizeMin.trim() !== '' && Number.isFinite(min) && min >= 0) size.min = min;
    if (nextSizeMax.trim() !== '' && Number.isFinite(max) && max > 0) size.max = max;

    const geoList = nextGeo
      .split(',')
      .map((g) => g.trim())
      .filter(Boolean);

    // Roles ride as the picked subset of the template blanket — the template
    // spec (matchMode, synonyms) stays authoritative on the backend.
    const roleSpecs: RoleSpec[] = templateRoles
      .filter((r) => nextSelected.includes(r.title))
      .map((r) => ({ title: r.title, matchMode: r.matchMode }));

    const specifics: Layer3Specifics = {
      ...(Object.keys(size).length > 0 ? { size } : {}),
      geo: geoList,
      roles: roleSpecs,
    };

    onChange(specifics);

    // Valid for composing when at least one template role is picked.
    onValidityChange(roleSpecs.length > 0);
  };

  // Reset the role selection to the full blanket whenever the template (or
  // its role set, e.g. after an inline edit) changes.
  const rolesKey = `${templateId ?? ''}|${templateRoles.map((r) => r.title).join('|')}`;
  useEffect(() => {
    const all = templateRoles.map((r) => r.title);
    setSelectedTitles(all);
    emit(sizeMin, sizeMax, geo, all);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rolesKey]);

  const toggleRole = (title: string) => {
    const next = selectedTitles.includes(title)
      ? selectedTitles.filter((t) => t !== title)
      : [...selectedTitles, title];
    setSelectedTitles(next);
    emit(sizeMin, sizeMax, geo, next);
  };

  const handleSizeMin = (value: string) => {
    setSizeMin(value);
    emit(value, sizeMax, geo, selectedTitles);
  };
  const handleSizeMax = (value: string) => {
    setSizeMax(value);
    emit(sizeMin, value, geo, selectedTitles);
  };
  const handleGeo = (value: string) => {
    setGeo(value);
    emit(sizeMin, sizeMax, value, selectedTitles);
  };

  // batch size is operator-only state (not emitted to the backend).
  const handleBatchSize = (value: string) => {
    const digits = value.replace(/[^0-9]/g, '');
    setBatchSize(digits);
  };

  return (
    <section className="card" aria-labelledby="layer3-heading">
      <div className="mb-3">
        <h2 id="layer3-heading" className="text-lg font-semibold text-text">
          Layer 3 - per-search specifics
        </h2>
        <p className="text-xs text-muted mt-0.5">
          Different every run. Size and geo - roles and keywords come from the layer-2 template.
        </p>
      </div>

      <div className="space-y-4">
        {/* Size band */}
        <div className="grid gap-3 sm:grid-cols-3">
          <label className="block">
            <span className="text-xs text-muted">Min employees</span>
            <input
              type="number"
              className="input mt-1"
              value={sizeMin}
              onChange={(e) => handleSizeMin(e.target.value)}
              placeholder="0"
              min={0}
              disabled={disabled}
            />
          </label>
          <label className="block">
            <span className="text-xs text-muted">Max employees</span>
            <input
              type="number"
              className="input mt-1"
              value={sizeMax}
              onChange={(e) => handleSizeMax(e.target.value)}
              placeholder="500"
              min={1}
              disabled={disabled}
            />
          </label>
          <label className="block">
            <span className="text-xs text-muted">Batch size (results cap)</span>
            <input
              type="number"
              className="input mt-1"
              value={batchSize}
              onChange={(e) => handleBatchSize(e.target.value)}
              placeholder="20"
              min={1}
              disabled={disabled}
              title="Operator reference only - not yet wired to the backend ingest cap"
            />
          </label>
        </div>
        <p className="text-xs text-muted -mt-2">
          Batch size is captured for reference but is not yet wired to the backend ingest cap.
        </p>

        {/* Geo */}
        <label className="block">
          <span className="text-xs text-muted">Geography (comma-separated)</span>
          <input
            className="input mt-1"
            value={geo}
            onChange={(e) => handleGeo(e.target.value)}
            placeholder="e.g. London, Manchester"
            disabled={disabled}
          />
        </label>

        {/* Roles — subset selection from the layer-2 blanket */}
        <div>
          <span className="text-xs text-muted">Roles this run targets *</span>
          {templateId === null ? (
            <p className="text-sm text-muted italic mt-1">
              Select a layer-2 class template to pick its roles.
            </p>
          ) : templateRoles.length === 0 ? (
            <p className="text-sm text-warning mt-1">
              The selected template has no roles - edit the template to add its role blanket.
            </p>
          ) : (
            <>
              <ul className="space-y-2 mt-1">
                {templateRoles.map((role) => (
                  <li key={role.title}>
                    <label className="flex items-center gap-2 text-sm text-text">
                      <input
                        type="checkbox"
                        checked={selectedTitles.includes(role.title)}
                        onChange={() => toggleRole(role.title)}
                        disabled={disabled}
                      />
                      <span>{role.title}</span>
                      <span
                        className={`badge ${
                          role.matchMode === 'loose' ? 'badge-secondary' : 'badge-primary'
                        }`}
                      >
                        {role.matchMode}
                      </span>
                      {role.synonyms && role.synonyms.length > 0 && (
                        <span className="text-xs text-muted">+{role.synonyms.length} synonym(s)</span>
                      )}
                    </label>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-muted mt-1">
                Pick which of the template&apos;s roles this run targets. Match mode and confirmed
                synonyms come from the template.
              </p>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
